import { Anchor, Avatar, Badge, Box, Divider, Grid, Group, Text, Textarea } from "@mantine/core"
import { MoneyIcon, ImageIcon, ReceiptIcon, DownloadSimpleIcon } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next"
import { IAdditionalLine } from '../../models/AdditionalLine'
import { IAttachment, IAttachmentPlus } from '../../models/Attachment'
import { ILine } from '../../models/Line'
import { currencyFormat, lineNetAmount } from "../../utils"
import { dateTimeSecFormat } from "../../utils/date"
import { renderName } from "../../utils/render-name"
import { OrderLedger } from "../order/order-ledger"
import { DotTag } from "./status-tag"
import { ColumnType, Table } from "../table"
import { FitTable } from "../order/fit-table"

type Props = {
  action: any
  // Supplier-only: when true, the order-quantity column also surfaces the
  // supplier's available stock balance for each line. Off by default so other
  // reuses of this table (order create/edit, buyer-facing views) stay unchanged.
  isSupplier?: boolean
  // Supplier PO detail (Ord_PurchaseDetail): tables that fit the card (fixed layout,
  // wide product column, no horizontal scroll), read-only grey notes and plain
  // ledger rows. Off by default so order create/edit keeps its look.
  fit?: boolean
}

/**
 * Widths for the line columns in `fit` mode, after «#» is dropped: product takes what is left.
 * FitTable cells are border-box, so each width includes the cell's 16px padding.
 */
function fitLineColumns(columns: ColumnType<ILine>[], isSupplier?: boolean): ColumnType<ILine>[] {
  const widths: (number | undefined)[] = isSupplier ? [undefined, 76, 116, 116, 124, 104, 112] : [undefined, 76, 116, 116, 104, 112]
  return columns.slice(1).map((column, i) => {
    const width = widths[i]
    if (!width) return column
    return { ...column, width, align: "right", titleAlign: "right", render: (record, index) => <Group justify="flex-end" wrap="nowrap">{column.render(record, index)}</Group> }
  })
}

/** Additional-line columns in `fit` mode: «#» dropped, name takes what is left. */
function fitAdditionalColumns(columns: ColumnType<IAdditionalLine>[]): ColumnType<IAdditionalLine>[] {
  const widths: (number | undefined)[] = [undefined, 72, 120, 80, 120, 100, 120]
  return columns.slice(1).map((column, i) =>
    widths[i]
      ? { ...column, width: widths[i] }
      // The name wraps inside its cell instead of the max-content width of the default table.
      : { ...column, render: (record) => <Text fz={14} c="dark.7">{record?.name ?? "-"}</Text> },
  )
}

export function PurchaseSalesDynamicDetailForm({ action, isSupplier, fit = false }: Props) {
  const { t } = useTranslation(["order", "common"])
  const now_date = new Date()
  const columns = useHeader({
    t,
    onClick: (key, record) => { },
    isSupplier,
  })

  const columnsAdditional = useHeaderAdditional({
    t,
    onClick: (key, record) => { },
  })

  const columnsAttachments = useHeaderAttachments({
    t,
    onClick: (key, item) => { },
  })

  const len = Object.keys((action && action[1]?.lines) || [])?.length

  const additionalLines = (action && action[1]?.additionalLines) || []
  const attachments = (action && action[1]?.attachments) || []

  return (
    <Box>
      {fit ? (
        <FitTable columns={fitLineColumns(columns, isSupplier)} rows={(action && action[1]?.lines) || []} rowKey={(row, i) => row?.id ?? String(i)} />
      ) : (
        <Table columns={columns} name="supplier-sales-order-list.lines" dataSource={action && (action[1].lines || [])} pagination={false} />
      )}

      {fit && additionalLines.length > 0 && (
        <Box mt="md">
          <Text fw={700} fz={16} mb="xs" px="lg">
            {t("order:purchaseSales.additional")}
          </Text>
          <FitTable columns={fitAdditionalColumns(columnsAdditional)} rows={additionalLines} rowKey={(row, i) => row?.id ?? String(i)} />
        </Box>
      )}

      {!fit && additionalLines.length > 0 && (
        <Box mt="xl">
          <Text fw={700} fz={16} mb="sm">
            {t("order:purchaseSales.additional")}
          </Text>
          <Table
            columns={columnsAdditional}
            name="supplier-sales-order-list.additionalLines"
            dataSource={additionalLines}
            pagination={false}
          />
        </Box>
      )}

      {fit ? (
        <Divider color="gray.2" />
      ) : (
        <>
          <Divider color="gray.3" my="xl" />
          <Text fw={700} fz={16} mb="sm">
            {t("order:purchaseSales.orderSummary")}
          </Text>
        </>
      )}
      <Grid mb={5} mt="md" px={fit ? "lg" : undefined} pb={fit ? "md" : undefined} gap={fit ? "xl" : undefined}>
        <Grid.Col span={{ xs: 12, sm: 12, md: fit ? 7 : 6 }}>
          <Grid>
            <Grid.Col span={12}>
              <Textarea
                value={(action && action[1]?.senderNote) || ""}
                label={t("order:purchaseSales.customerNote")}
                onChange={() => { }}
                {...(fit ? { variant: "filled", readOnly: true, autosize: true, minRows: 2 } : {})}
              />
            </Grid.Col>
            <Grid.Col span={12}>
              <Textarea
                value={fit ? (action && action[1]?.senderAdditionalNote) || "" : (action && action[1]?.senderAdditionalNote) || t("order:purchaseSales.additionalNote")}
                label={t("order:purchaseSales.enterNote")}
                onChange={() => { }}
                {...(fit ? { variant: "filled", readOnly: true, autosize: true, minRows: 2 } : {})}
              />
            </Grid.Col>
          </Grid>
        </Grid.Col>
        <Grid.Col span={{ xs: 12, sm: 12, md: fit ? 5 : 6 }}>
          {(() => {
            const lines = action?.[1]?.lines || []
            const lineTotal = lines.reduce((total: number, item: any) => total + (item.quantity * item.price || 0), 0)
            const lineVat = lines.reduce((total: number, item: any) => total + (item.quantity * item.vatAmount || 0), 0)
            const lineTax = lines.reduce((total: number, item: any) => total + (item.quantity * item.taxAmount || 0), 0)
            const lineDiscount = lines.reduce((total: number, item: any) =>
              total +
              ((item?.discountType === "PERCENTAGE"
                ? (item?.price * item?.quantity * item?.discountValue) / 100
                : item?.quantity * item?.discountValue) || 0), 0)
            const shippingAmount = action?.[1]?.shippingAmount || 0
            const discountType = action?.[1]?.discountType
            const discountValue = action?.[1]?.discountValue || 0
            // The order-level figures come STRAIGHT OFF THE ORDER — they are not
            // recomputed here, because recomputing them is what made this ledger
            // disagree with the invoice the buyer is actually sent.
            //
            // de-order-service order/create.ts builds ORDER_AMOUNT from the lines
            // plus the additional lines only (line 553-556), applies the
            // payment-term rule to THAT (line 573), and adds the shipping fee
            // AFTER (line 621). It persists the pair as `orderAmount` (pre-discount)
            // and `totalAmount` (post-discount, shipping included). Prices are
            // VAT-INCLUSIVE, so the extracted VAT and city tax below are already
            // inside `lineTotal` and must never be added on top of it.
            //
            // Hence: grand total IS `totalAmount`, and the deduction is exactly
            // what the two headers differ by once the shipping fee is taken back
            // out of the net side. `shippingAmount` is 0 on a PURCHASE order
            // (create.ts:662), so the term does nothing there.
            const orderAmount = action?.[1]?.orderAmount || 0
            const totalAmount = action?.[1]?.totalAmount || 0
            const orderDiscountAmount = Math.max(0, orderAmount - totalAmount + shippingAmount)
            const grandTotal = lines.length ? totalAmount : 0
            const totalQty = lines.reduce((total: number, item: any) => total + (item.quantity || 0), 0)
            const isCod = action?.[1]?.paymentTerm?.condition === "COD"
            const paymentConfirmAmount = isCod && lines.length
              ? (grandTotal * (action?.[1]?.paymentTerm?.advancePercent || 0)) / 100
              : null
            const paymentConfirmDate = isCod
              ? dateTimeSecFormat(now_date.setDate(now_date.getDate() + (action?.[1]?.paymentTerm?.confirmationDay || 0)))
              : "-"
            // Money first, rate as a suffix. This row is the buyer's only evidence
            // of what came off the order, and every other row in the ledger is a
            // currency value; a bare "10 %" told the reader nothing (and carried no
            // sign either — order-ledger.tsx's `negative` only applies a CSS class).
            const priceDiscountDisplay = discountType
              ? `− ${currencyFormat(orderDiscountAmount)}${discountType === "PERCENTAGE" ? ` (${discountValue}%)` : ""}`
              : "-"
            // B.5 — the order-level discount comes from the payment term the buyer
            // picked, so name that term in the label instead of adding a second row.
            // Plain label when the order carries no discount or no payment term.
            const paymentTermDescription = action?.[1]?.paymentTerm?.description
            const priceDiscountLabel = discountType && paymentTermDescription
              ? `${t("order:purchaseSales.priceDiscount")} (${paymentTermDescription})`
              : t("order:purchaseSales.priceDiscount")

            // Ord_PurchaseDetail shows the counts as the first two plain rows, without the stat strip and heading.
            const countRows = fit
              ? [
                { label: t("order:purchaseSales.orderCount"), value: `${len} ${t("order:purchaseSales.type").toLowerCase()}` },
                { label: t("order:purchaseSales.totalQuantity"), value: `${totalQty} ${t("order:purchaseSales.piece")}` },
              ]
              : []

            return (
              <OrderLedger
                compact={fit}
                stats={fit ? undefined : [
                  { value: len, label: t("order:purchaseSales.type") },
                  { value: totalQty, label: t("order:purchaseSales.piece") },
                ]}
                headLabel={fit ? undefined : t("order:calculation")}
                headIcon={<ReceiptIcon size={14} color="var(--mantine-color-brand-6)" />}
                rows={[
                  ...countRows,
                  { label: t("order:purchaseSales.totalOrderAmount"), value: currencyFormat(lineTotal) },
                  { label: t("order:purchaseSales.calculatedVat"), value: currencyFormat(lineVat), muted: true },
                  { label: t("order:purchaseSales.calculatedTax"), value: currencyFormat(lineTax), muted: true },
                  { label: t("order:purchaseSales.calculatedDiscount"), value: `− ${currencyFormat(lineDiscount)}`, negative: true },
                  { label: t("order:purchaseSales.shippingFee"), value: currencyFormat(shippingAmount) },
                  { label: priceDiscountLabel, value: priceDiscountDisplay, negative: !!discountType },
                ]}
                grand={{
                  label: t("order:purchaseSales.totalAmount"),
                  value: currencyFormat(grandTotal),
                }}
                // Ord_PurchaseDetail ends the summary at «Нийт дүн»; the order form keeps the payment rows.
                payRows={fit ? undefined : [
                  {
                    icon: <MoneyIcon size={14} color="var(--mantine-color-brand-6)" />,
                    label: t("order:purchaseSales.paymentConfirm"),
                    value: paymentConfirmAmount !== null ? currencyFormat(paymentConfirmAmount) : "—",
                  },
                  {
                    label: t("order:purchaseSales.paymentConfirmDate"),
                    value: paymentConfirmDate,
                  },
                ]}
              />
            )
          })()}
        </Grid.Col>
      </Grid>
      {attachments.length > 0 && (
        <Box mt="xl" pb={fit ? "md" : undefined}>
          <Text fw={700} fz={16} mb="sm" px={fit ? "lg" : undefined}>
            {t("order:purchaseSales.addAttachment")}
          </Text>
          {fit ? (
            <FitTable columns={columnsAttachments.slice(1)} rows={attachments} rowKey={(row, i) => row?.id ?? String(i)} />
          ) : (
            <Table columns={columnsAttachments} name="values.attachments" dataSource={attachments} pagination={false} />
          )}
        </Box>
      )}
    </Box>
  )
}

const useHeader = ({ t, onClick, isSupplier }: { t: any; onClick: (key: string, record: ILine) => void; isSupplier?: boolean }): ColumnType<ILine>[] => [
  {
    title: "#",
    render: (_r, index) => <Text size="sm">{index + 1}</Text>,
  },
  {
    title: t("order:purchaseSales.productService"),
    dataIndex: "name",
    render: (record) => (
      <Group gap="sm" wrap="nowrap">
        <Avatar src={record?.image || null} radius="sm" size={44}>
          <ImageIcon />
        </Avatar>
        <Box>
          <Text fz={14} fw={600} c="var(--mantine-color-dark-7)">{record?.name ?? "-"}</Text>
          <Text fz={14} c="gray.8" ff="monospace">{record?.skuCode ?? "-"}</Text>
        </Box>
      </Group>
    ),
  },
  {
    title: t("order:purchaseSales.unit"),
    dataIndex: "unit.name",
    render: (record) => {
      return (
        <Text fz={14} c="var(--mantine-color-dark-5)" w="max-content">
          {record?.unit ?? "-"}
        </Text>
      )
    },
  },
  {
    title: t("order:purchaseSales.unitPrice"),
    dataIndex: "price",
    render: (record) => {
      return (
        <Text fz={14} c="var(--mantine-color-dark-5)" style={{ whiteSpace: "nowrap" }} ta="end">
          {currencyFormat(record?.price ?? 0)}
        </Text>
      )
    },
  },
  {
    title: t("order:purchaseSales.orderQuantity"),
    dataIndex: "quantity",
    render: (record) => {
      return (
        <Box>
          <Text fz={14} w="max-content">{record.quantity ?? "0"}</Text>
          {record.backorderable && (
            <Badge size="md" tt="none" variant="light" color="gray">{t("order:purchaseSales.backorderColumn")}</Badge>
          )}
        </Box>
      )
    },
  },
  // Supplier-only: dedicated stock-balance column placed right after the order
  // quantity. Sum of available quantity across the supplier's warehouses for
  // this line's variant (backend scopes `inventories` to the supplier
  // business). Green when stock covers the ordered amount, red otherwise — same
  // convention as the buyer card. Off for other reuses (order create/edit,
  // buyer-facing views) so those tables stay unchanged.
  ...(isSupplier
    ? [
        {
          title: t("order:purchaseSales.remaining"),
          dataIndex: "remaining",
          render: (record: ILine) => {
            const ordered = Number(record?.quantity) || 0
            const remaining = ((record as any)?.inventories || []).reduce(
              (sum: number, inv: any) => sum + (Number(inv?.availableQuantity) || 0),
              0,
            )
            return (
              <DotTag
                color={remaining >= ordered ? "green" : "orange"}
                label={t("order:purchaseSales.stockReady", { count: remaining })}
              />
            )
          },
        } as ColumnType<ILine>,
      ]
    : []),
  {
    title: t("order:purchaseSales.vat"),
    dataIndex: "hasVat",
    render: (record) => {
      return (
        <Text fz={14} c="var(--mantine-color-dark-5)" style={{ whiteSpace: "nowrap" }} ta="end">
          {currencyFormat(record?.vatAmount ?? 0)}
        </Text>
      )
    },
  },
  {
    title: t("order:purchaseSales.amount"),
    dataIndex: "name",
    render: (record) => {
      // The stored line total: post line-discount and post the line's share of
      // the order-level discount, exactly as discount-allocation.ts left it.
      // It used to be `price * quantity + vatAmount`, which added one unit of
      // an already-included VAT on top of the whole line (DSC-001).
      const count = Number(record?.totalAmount) || lineNetAmount(record)
      return (
        <Text fz={14} c="var(--mantine-color-dark-5)" style={{ whiteSpace: "nowrap" }} ta="end">
          {currencyFormat(count ?? 0)}
        </Text>
      )
    },
  },
]

const useHeaderAdditional = ({ t, onClick }: { t: any; onClick: (key: string, record: IAdditionalLine) => void }): ColumnType<IAdditionalLine>[] => [
  {
    title: "#",
    render: (_r, index) => <Text size="sm">{index + 1}</Text>,
  },
  {
    title: t("order:purchaseSales.itemName"),
    dataIndex: "name",
    render: (record) => {
      return (
        <Text fz={14} c="var(--mantine-color-dark-5)" w="max-content">
          {record?.name ?? "-"}
        </Text>
      )
    },
  },
  {
    title: t("order:purchaseSales.unit"),
    dataIndex: "unit.name",
    render: (record) => {
      return (
        <Text fz={14} c="var(--mantine-color-dark-5)" w="max-content">
          {record?.unit ?? "-"}
        </Text>
      )
    },
  },
  {
    title: t("order:purchaseSales.unitPrice"),
    dataIndex: "price",
    render: (record) => {
      return (
        <Text fz={14} c="var(--mantine-color-dark-5)" style={{ whiteSpace: "nowrap" }} ta="end">
          {currencyFormat(record?.price || 0)}
        </Text>
      )
    },
  },
  {
    title: t("order:purchaseSales.quantity"),
    dataIndex: "quantity",
    render: (record) => {
      return (
        <Text size="sm" w="max-content">
          {record.quantity ?? "0"}
        </Text>
      )
    },
  },
  {
    title: t("order:purchaseSales.discountType"),
    dataIndex: "discountType",
    render: (record) => {
      return (
        <Text size="sm" w="max-content">
          {record.discountType === "PERCENTAGE" && t("order:purchaseSales.discountPercent")}
          {record.discountType === "AMOUNT" && t("order:purchaseSales.discountAmount")}
          {!record?.discountType && "-"}
        </Text>
      )
    },
  },
  {
    title: t("order:purchaseSales.discount"),
    dataIndex: "discountType",
    render: (record) => {
      return (
        <>
          <Text c="var(--mantine-color-dark-5)" w="max-content">
            {record.discountType === "PERCENTAGE" && `${record?.discountValue} %`}
          </Text>
          <Text style={{ whiteSpace: "nowrap" }} ta="end">
            {record.discountType === "AMOUNT" && currencyFormat(record?.discountValue ?? 0)}
          </Text>
          <Text fz={14} c="var(--mantine-color-dark-5)" w="max-content">
            {!record?.discountType && "-"}
          </Text>
        </>
      )
    },
  },
  {
    title: t("order:purchaseSales.amount"),
    dataIndex: "name",
    render: (record: any) => {
      let count
      count = (record?.price || 0) * (record?.quantity || 0)
      return (
        <Text size="sm" fw={500} c="var(--mantine-color-dark-5)" style={{ whiteSpace: "nowrap" }} ta="end">
          {currencyFormat(count ?? 0)}
        </Text>
      )
    },
  },
]

const useHeaderAttachments = ({ t, onClick }: { t: any; onClick: (key: string, record: IAttachment) => void }): ColumnType<IAttachmentPlus>[] => [
  {
    title: "#",
    render: (_r, index) => <Text size="sm">{index + 1}</Text>,
  },
  {
    title: t("order:purchaseSales.name"),
    dataIndex: "name",
    render: (record) => {
      return (
        <Text fz={14} c="var(--mantine-color-dark-5)" w="max-content">
          {record?.name ?? "-"}
        </Text>
      )
    },
  },
  {
    title: t("order:purchaseSales.url"),
    dataIndex: "url",
    render: (record) =>
      record?.url ? (
        <Anchor href={record.url} target="_blank" rel="noopener noreferrer" download size="sm" c="brand" w="max-content">
          <Group gap={6} wrap="nowrap">
            <DownloadSimpleIcon size={14} />
            {t("order:purchaseSales.download", { defaultValue: "Татах" })}
          </Group>
        </Anchor>
      ) : (
        <Text size="sm" c="dimmed">-</Text>
      ),
  },
  {
    title: t("order:purchaseSales.description"),
    dataIndex: "description",
    render: (record) => {
      return (
        <Text fz={14} c="var(--mantine-color-dark-5)" w="max-content">
          {record?.description ?? "-"}
        </Text>
      )
    },
  },
  {
    title: t("order:purchaseSales.attachedBy", { defaultValue: "Хавсаргасан" }),
    dataIndex: "attachedUser",
    render: (record) => (
      <Text fz={14} c="var(--mantine-color-dark-5)" w="max-content">
        {renderName(record?.attachedUser)}
      </Text>
    ),
  },
  {
    title: t("order:purchaseSales.date", { defaultValue: "Огноо" }),
    dataIndex: "createdAt",
    render: (record) => (
      <Text fz={14} c="var(--mantine-color-dark-5)" w="max-content" style={{ fontVariantNumeric: "tabular-nums" }}>
        {record?.createdAt ? dateTimeSecFormat(record.createdAt) : "-"}
      </Text>
    ),
  },
]
